import classNames from 'classnames';
import I18n from 'i18n-js';
import React from 'react';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import Currency from '@reverbdotcom/commons/src/currency';
import FormGroupWithCheckbox from '@reverbdotcom/commons/src/components/form_group_with_checkbox';
import FormGroupWithInput from '@reverbdotcom/commons/src/components/form_group_with_input';

import { INPUT_NAME_PREFIXES, FormContext } from './edit_shipping_rate_card';
import URLHelpers from './url_helpers';
import { core_apimessages_Money } from '../../schemas/rql';

export interface IShippingRate {
  isRequired?: boolean;
  regionType?: string;
  regionCode?: string;
  regionName?: string;
  rate?: core_apimessages_Money;
  expeditedRate?: core_apimessages_Money;
  incrementalRate?: core_apimessages_Money;
  freeShippingThreshold?: core_apimessages_Money;
}

// The names of the individual money fields on a ShippingRate object, as returned by our backend.
export enum PriceField {
  STANDARD = 'rate',
  COMBINED = 'incrementalRate',
  EXPEDITED = 'expeditedRate',
}

// This component currently renders inputs within haml forms, and uses input names expected by shipping profile/listing rails controllers.
// Once shipping rates can be submitted via a mutation, this naming can be stripped out/simplified.
const PRICE_INPUT_NAMES = {
  [PriceField.STANDARD]: '[rate]',
  [PriceField.COMBINED]: '[incremental_rate]',
  [PriceField.EXPEDITED]: '[expedited_rate]',
};

interface IProps {
  formContext: FormContext;
  shippingRate?: IShippingRate;
  handleEditShippingRate?: (IShippingRate) => void;
  currency: string;
  allowedToShipExpedited: boolean;
}

export function priceInputErrorMessage(shippingRate: IShippingRate, forField: PriceField): string {
  const standardAmount = shippingRate[PriceField.STANDARD]?.amount || '';
  const combinedAmount = shippingRate[PriceField.COMBINED]?.amount || '';
  const expeditedAmount = shippingRate[PriceField.EXPEDITED]?.amount || '';

  switch (forField) {
    case PriceField.STANDARD:
      if (!standardAmount) {
        return I18n.t('discovery.dashboard.selling.shippingRates.rateFields.validationErrors.standardRequired');
      }

      break;
    case PriceField.COMBINED:
      if ((standardAmount && combinedAmount) && parseFloat(combinedAmount) >= parseFloat(standardAmount)) {
        return I18n.t('discovery.dashboard.selling.shippingRates.rateFields.validationErrors.combinedGreaterThanStandard');
      }

      break;
    case PriceField.EXPEDITED:
      if ((standardAmount && expeditedAmount) && (parseFloat(expeditedAmount) < parseFloat(standardAmount))) {
        return I18n.t('discovery.dashboard.selling.shippingRates.rateFields.validationErrors.expeditedLessThanStandard');
      }

      break;
    default:
      return '';
  }

  return '';
}

function ManualPriceEntry({
  formContext,
  rateType,
  value,
  onUpdate,
  currency,
  error,
  tooltipText = '',
  indented = false,
  required = false,
}: {
  formContext: FormContext
  rateType: PriceField;
  value: string;
  onUpdate: (string) => void;
  currency: string;
  error: string;
  tooltipText?: string;
  indented?: boolean;
  required?: boolean;
}) {
  const [hasBeenTouched, setHasBeenTouched] = React.useState(Boolean(value));
  React.useEffect(() => {
    if (value) {
      setHasBeenTouched(true);
    }
  }, [value]);

  const inputName = `${INPUT_NAME_PREFIXES[formContext]}${PRICE_INPUT_NAMES[rateType]}`;

  const className = classNames(
    'shipping-rate-card__manual__price__entry',
    { 'shipping-rate-card__manual__price__entry--indented': indented },
  );

  return (
    <div className={className}>
      <div className="shipping-rate-card__manual__price__entry__input">
        <FormGroupWithInput
          inputName={inputName}
          label={I18n.t(`discovery.dashboard.selling.shippingRates.rateFields.${rateType}`)}
          labelTooltipText={tooltipText}
          value={value}
          updateField={(e) => onUpdate(e[inputName])}
          currencyPrefix={Currency.get(currency).symbol}
          currencySuffix={currency}
          numberOnly
          errorText={hasBeenTouched && error}
          required={required}
        />
      </div>
    </div>
  );
}

export function EditShippingRateCardManualPrices({
  formContext,
  shippingRate,
  handleEditShippingRate,
  currency,
  allowedToShipExpedited,
}: IProps) {
  const [isExpeditedShippingEnabled, setIsExpeditedShippingEnabled] = React.useState(!!shippingRate[PriceField.EXPEDITED]?.amount);
  const [isCombinedShippingEnabled, setIsCombinedShippingEnabled] = React.useState(!!shippingRate[PriceField.COMBINED]?.amount);

  const forDomesticRegion = shippingRate.isRequired;
  const forShippingProfile = formContext === FormContext.SHIPPING_PROFILE;
  const showExpeditedControls = forDomesticRegion && forShippingProfile;

  return (
    <div className="shipping-rate-card__manual__prices">
      <ManualPriceEntry
        formContext={formContext}
        rateType={PriceField.STANDARD}
        value={shippingRate[PriceField.STANDARD]?.amount || ''}
        onUpdate={(v) => handleEditShippingRate({
          ...shippingRate,
          [PriceField.STANDARD]: v ? { amount: v } : null,
        })}
        currency={currency}
        error={priceInputErrorMessage(shippingRate, PriceField.STANDARD)}
        tooltipText={isCombinedShippingEnabled && I18n.t('discovery.dashboard.selling.shippingRates.rateFields.standardShippingTooltip')}
        required={formContext === FormContext.LISTING}
      />

      <FormGroupWithCheckbox
        data-combined-toggle
        inputName={`enable_combined_${shippingRate.regionCode}`}
        label={I18n.t('discovery.dashboard.selling.shippingRates.rateFields.enableCombined')}
        helpText={I18n.t('discovery.dashboard.selling.shippingRates.rateFields.enableCombinedHelp')}
        checked={isCombinedShippingEnabled}
        updateField={() => setIsCombinedShippingEnabled(!isCombinedShippingEnabled)}
      />

      {isCombinedShippingEnabled && (
        <ManualPriceEntry
          formContext={formContext}
          rateType={PriceField.COMBINED}
          value={shippingRate[PriceField.COMBINED]?.amount || ''}
          onUpdate={(v) => handleEditShippingRate({
            ...shippingRate,
            [PriceField.COMBINED]: v ? { amount: v } : null,
          })}
          currency={currency}
          tooltipText={I18n.t('discovery.dashboard.selling.shippingRates.rateFields.combinedShippingTooltip')}
          error={priceInputErrorMessage(shippingRate, PriceField.COMBINED)}
          indented
        />
      )}

      {showExpeditedControls && (
        <>
          <FormGroupWithCheckbox
            data-expedited-toggle
            disabled={!allowedToShipExpedited}
            inputName={`enable_expedited_${shippingRate.regionCode}`}
            label={(
              <>
                {I18n.t(`discovery.dashboard.selling.shippingRates.rateFields.expeditedLabel.${allowedToShipExpedited ? 'enabled' : 'disabled'}`)}
                <CoreLink
                  to={URLHelpers.expeditedShippingHelpPath}
                  target="_blank"
                  className="ml-space"
                >
                  {I18n.t(`discovery.dashboard.selling.shippingRates.rateFields.expeditedLearn.${allowedToShipExpedited ? 'enabled' : 'disabled'}`)}
                </CoreLink>
              </>
            )}
            updateField={() => setIsExpeditedShippingEnabled(!isExpeditedShippingEnabled)}
            checked={isExpeditedShippingEnabled}
          />

          {isExpeditedShippingEnabled && (
            <ManualPriceEntry
              formContext={formContext}
              rateType={PriceField.EXPEDITED}
              value={shippingRate[PriceField.EXPEDITED]?.amount || ''}
              onUpdate={(v) => handleEditShippingRate({
                ...shippingRate,
                [PriceField.EXPEDITED]: v ? { amount: v } : null,
              })}
              currency={currency}
              error={priceInputErrorMessage(shippingRate, PriceField.EXPEDITED)}
              indented
            />
          )}
        </>
      )}
    </div>
  );
}

export default EditShippingRateCardManualPrices;
